import { isMemoryBook } from '../../../../../util/album';
import { EVENT_ADDON_ITEM_TYPE } from '../../../../../constants/event-items';
import { giBToBytes } from '../../Upload/VideoUpload/helpers';

/**
 * Checks whether the upload targets contain Photo Finder items.
 *
 * @param {Object} options The options.
 * @param {Object} options.albumFolder The AlbumFolder.
 * @param {Array<Object>} options.targets The upload targets.
 * @returns {boolean}
 */
export const uploadTargetsContainPhotoFinderCredits = ({
  albumFolder,
  targets = [],
}) => {
  if (!albumFolder && !targets.length) {
    return false;
  }

  const containsPhotoFinderAlbums = targets.some(
    target => target.album?.org_pay_photo_pricing_enabled,
  );

  return containsPhotoFinderAlbums || albumFolder.org_pay_photo_pricing_enabled;
};

/**
 * Determines whether a user can upload to a PFC AlbumFolder (based on PF credits available on the account).
 *
 * @param {Object} options The options.
 * @param {Object} options.account The Account.
 * @param {Object} options.albumFolder The AlbumFolder.
 * @param {Number} [options.uploadLength] The number of uploaded photos.
 * @param {Array<Object>} [options.targets] The upload targets.
 * @returns {boolean}
 */
export const uploadBlockedByPhotoFinderCredits = ({
  account,
  albumFolder,
  uploadLength,
  targets = [],
}) => {
  if (
    !albumFolder ||
    !account ||
    albumFolder?.purchased_event_addon_types?.length > 0
  ) {
    return false;
  }

  if (account.pro_settings?.photo_finder_credits_waived) {
    return false;
  }

  const containsPhotoFinderTargets = uploadTargetsContainPhotoFinderCredits({
    albumFolder,
    targets,
  });
  const accountTotalCredits = account.photo_finder_credits_balance?.total ?? 0;
  const accountUsedCredits =
    account.photo_finder_photos_count_pending_charge ?? 0;
  const accountAvailableCredits = accountTotalCredits - accountUsedCredits;

  // if there are no PF targets ignore this.
  if (!containsPhotoFinderTargets) {
    return false;
  }

  // Else, check whether the available PFC credits are enough to perform the upload.
  return uploadLength > accountAvailableCredits;
};

/**
 * Determines whether a user can upload based on their storage limits.
 *
 * @param {Object} options The options.
 * @param {Object} [options.account] The Account.
 * @returns {boolean}
 */
export const uploadBlockedByStorageLimit = ({ account }) => {
  // If the user has a free PhotoManager subscription we do not cap storage.
  // Accounts flagged to skip the storage exceeded prompt are not locked either.
  if (
    !account ||
    account?.pro_settings?.has_free_photo_manager ||
    account?.pro_settings?.skip_storage_exceeded_prompt
  ) {
    return false;
  }

  return account.storage?.used >= account.storage?.max;
};

/**
 * Returns a base account item (to be used with uploadBlockedByStorageLimit and uploadBlockedByPhotoFinderCredits)
 * based on the contributor assignment's upload info.
 *
 * @param {Object} contributorAssignment The contributor assignment record.
 * @param {Object} [ownerAccount] The owner Account record (e.g. `albumFolder.account`),
 *  used to carry over the owner's pro settings.
 * @returns {{photo_finder_photos_count_pending_charge: (number|*), storage, photo_finder_credits_balance: *, pro_settings: *}|undefined}
 */
export const accountFromContributorUploadInfo = (
  contributorAssignment,
  ownerAccount,
) =>
  contributorAssignment?.upload_info
    ? {
        photo_finder_credits_balance:
          contributorAssignment.upload_info.photo_finder.credits_balance,
        photo_finder_photos_count_pending_charge:
          contributorAssignment.upload_info.photo_finder
            .photo_finder_photos_count_pending_charge,
        storage: contributorAssignment.upload_info.storage,
        pro_settings: ownerAccount?.pro_settings,
      }
    : undefined;

/**
 * Determines whether an albumFolder can upload based on their storage limits.
 *
 * @param {Object} options The options.
 * @param {Object} [options.albumFolder] The purchasedEvent item.
 * @returns {boolean}
 */
export const albumFolderUploadBlockedByStorageLimit = ({ albumFolder }) => {
  // If the user has a free PhotoManager subscription we do not cap storage
  if (!albumFolder) {
    return false;
  }

  return albumFolder.storage?.used >= albumFolder.storage?.max;
};

/**
 * Determines whether or not a given set of targets have video support
 *
 * @param {Object} options The options
 * @param {Array} [options.targetAlbums] the target albums
 * @returns {boolean}
 */
export const targetsSupportVideo = ({ targetAlbums }) => {
  if (!targetAlbums) {
    return false;
  }

  // Realistically, all albums but MSS support video uploads
  if (targetAlbums.find(({ album }) => !isMemoryBook(album))) {
    return true;
  }

  return false;
};

export const getAlbumFolderAvailablePhotoFinderCredits = albumFolder => {
  const albumFolderTotalCredits =
    albumFolder?.photo_finder.credits_balance?.total ?? 0;
  const albumFolderUsedCredits =
    albumFolder?.photo_finder.photo_finder_photos_count_pending_charge ?? 0;
  const albumFolderAvailableCredits =
    albumFolderTotalCredits - albumFolderUsedCredits;

  return albumFolderAvailableCredits;
};

/**
 * Determines whether a user can upload to a PFC AlbumFolder (based on album folder PF credits available).
 *
 * Note that account PF credits will not be considered for these cases.
 *
 * @param {Object} options The options.
 * @param {Object} options.albumFolder The AlbumFolder.
 * @param {Number} [options.uploadLength] The number of uploaded photos.
 * @returns {boolean}
 */
export const uploadBlockedByAlbumFolderPhotoFinderCredits = ({
  albumFolder,
  uploadLength,
}) => {
  if (
    !albumFolder ||
    albumFolder?.purchased_event_addon_types?.length === 0 ||
    !albumFolder?.purchased_event_addon_types?.find(type =>
      [
        EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER,
        EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER_PLUS,
      ].includes(type),
    )
  ) {
    return false;
  }

  // Check whether the available PFC credits are enough to perform the upload.
  return uploadLength > getAlbumFolderAvailablePhotoFinderCredits(albumFolder);
};

export const uploadBlockedByStorageSizeLimit = ({
  albumFolder,
  account,
  uploadSize,
  accountStorageFallback = false,
}) => {
  if (!albumFolder?.storage && !account?.storage) {
    return false;
  }

  const accountAvailableStorage =
    giBToBytes(account.storage?.max) - giBToBytes(account.storage.used);

  if (albumFolder?.storage?.max) {
    const availableStorage =
      giBToBytes(albumFolder.storage.max) -
      giBToBytes(albumFolder.storage.used);

    if (accountStorageFallback) {
      return uploadSize > availableStorage + accountAvailableStorage;
    }
    return uploadSize > availableStorage;
  }

  return uploadSize > accountAvailableStorage;
};
